import { ReactNode } from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ReturnsListTiles, ReturnsReasonCount, ReturnsTab } from "../../models/returns/IReturnsApi";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import { INBOX_DAY_TIME } from "../../utils/inboxDates";
import PriceFormat from "../../utils/priceFormat";

// Darkest first; anything past the sixth reason shares the last shade.
const BAR_SHADES = ["bg-slate-800", "bg-slate-600", "bg-slate-400", "bg-slate-300", "bg-slate-200", "bg-gray-200"];

function Tile({ title, value, sub, tone = "default", onClick }: { title: string; value: ReactNode; sub: string; tone?: "default" | "red" | "amber"; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="flex flex-col gap-0.5 rounded-xl border border-gray-200 bg-white px-3.5 py-3 text-left hover:border-gray-300">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{title}</span>
      <span className={classNames("text-2xl font-bold leading-tight", tone === "red" && "text-red-700", tone === "amber" && "text-amber-700", tone === "default" && "text-gray-900")}>{value}</span>
      <span className="text-[11px] text-gray-500">{sub}</span>
    </button>
  );
}

interface ReturnsTilesProps {
  tiles: ReturnsListTiles;
  reasons: ReturnsReasonCount[];
  onTab: (tab: ReturnsTab) => void;
}

/** Respond today · Expected back · Refund due, plus the "Why parts come back" bar for the last 90 days. */
export default function ReturnsTiles({ tiles, reasons, onTab }: ReturnsTilesProps) {
  const { t } = useTranslation("returns");
  const total = reasons.reduce((sum, r) => sum + r.count, 0);
  const shown = reasons.slice(0, BAR_SHADES.length);

  return (
    <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(200px, 100%), 1fr))" }}>
      <Tile
        title={t("Respond today")}
        value={tiles.respondToday}
        tone={tiles.respondToday > 0 ? "red" : "default"}
        sub={tiles.respondOverdue > 0 ? t("{{count}} overdue · eBay deadlines", { count: tiles.respondOverdue }) : t("eBay deadlines")}
        onClick={() => onTab("waiting_response")}
      />
      <Tile
        title={t("Expected back")}
        value={tiles.expectedBack}
        sub={t("{{posted}} posted · {{notPosted}} not yet posted", { posted: tiles.expectedPosted, notPosted: tiles.expectedNotPosted })}
        onClick={() => onTab(tiles.expectedPosted > 0 ? "in_transit" : "waiting_buyer")}
      />
      <Tile
        title={t("Refund due")}
        value={tiles.refundDue}
        tone={tiles.refundDue > 0 ? "amber" : "default"}
        sub={
          tiles.refundDue > 0
            ? `${PriceFormat(tiles.refundDueAmount)}${tiles.refundDueBy ? ` · ${t("by {{when}}", { when: formatDateTime(tiles.refundDueBy, INBOX_DAY_TIME) })}` : ""}`
            : t("Nothing waiting to be refunded")
        }
        onClick={() => onTab("refund_due")}
      />
      <div className="flex min-w-0 flex-col gap-2 rounded-xl border border-gray-200 bg-white px-3.5 py-3 md:col-span-2">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            {t("Why parts come back · last 90 days · {{count}} returns", { count: total })}
          </span>
          <Link to="/reports" className="text-[11px] font-semibold text-primary-700 hover:underline">
            {t("Report")} →
          </Link>
        </div>
        {total > 0 ? (
          <>
            <div className="flex h-3.5 gap-0.5 overflow-hidden rounded-full" role="img" aria-label={shown.map((r) => `${r.label} ${r.count}`).join(", ")}>
              {shown.map((r, i) => (
                <span key={r.reason} className={BAR_SHADES[i]} style={{ flex: r.count }} />
              ))}
            </div>
            <div className="flex flex-wrap gap-x-3.5 gap-y-1 text-[11px] text-gray-700">
              {shown.map((r, i) => (
                <span key={r.reason} className="flex items-center gap-1.5">
                  <span className={classNames("h-2 w-2 rounded-sm", BAR_SHADES[i])} />
                  {r.label} {r.count}
                </span>
              ))}
            </div>
          </>
        ) : (
          <span className="text-[12px] text-gray-500">{t("No returns in the last 90 days.")}</span>
        )}
      </div>
    </div>
  );
}
